/**
 * Feedback admin screen — closed-loop feedback Stage 3.
 *
 * End users submit complaints from the desktop Rewards Hub via
 * POST /au/feedback. Stage 2 (Claude auto-triage) handles the
 * pattern-match cases automatically; everything else lands here
 * for human review. The "Open" filter is the working queue —
 * what needs attention right now.
 *
 * Three things this screen has to do well:
 *  1. Make the open queue visible at a glance (stats overview).
 *  2. Make replying fast — type, hit Send, the user gets an
 *     email + their in-app card updates within a polling cycle.
 *  3. Show diagnostics + the auto-triage reasoning inline so the
 *     human can second-guess Claude's category decision before
 *     wasting cycles on a misclassified report.
 *
 * Polling: 60s on both list and stats; mutations invalidate both.
 */
import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import {
  AppRef,
  FeedbackCategory,
  FeedbackPlatform,
  FeedbackStatus,
  PublisherRef
} from '@/api/feedback';
import {
  useFeedback,
  useFeedbackOne,
  useFeedbackStats,
  useUpdateFeedback
} from '@/hooks/useFeedback';
import {
  ActionButton,
  Badge,
  Card,
  Pagination,
  SectionLabel,
  Select,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import { fmtDate, fmtDateTime } from '@/lib/format';

const STATUS_LABEL: Record<FeedbackStatus, string> = {
  open: 'Open',
  auto_replied: 'Auto-replied',
  in_progress: 'In progress',
  awaiting_user: 'Awaiting user',
  resolved: 'Resolved',
  spam: 'Spam'
};

const CATEGORY_LABEL: Record<FeedbackCategory, string> = {
  battery: 'Battery',
  performance: 'Performance',
  av: 'Antivirus',
  payouts: 'Payouts',
  redemption: 'Redemption',
  login: 'Login',
  ui: 'UI',
  other: 'Other',
  unknown: 'Unknown'
};

const STATUS_OPTIONS: Array<{ value: FeedbackStatus | ''; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'auto_replied', label: 'Auto-replied' },
  { value: 'awaiting_user', label: 'Awaiting user' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'spam', label: 'Spam' },
  { value: '', label: 'All statuses' }
];

const CATEGORY_OPTIONS: Array<{ value: FeedbackCategory | ''; label: string }> = [
  { value: '', label: 'All categories' },
  { value: 'battery', label: 'Battery' },
  { value: 'performance', label: 'Performance' },
  { value: 'av', label: 'Antivirus' },
  { value: 'payouts', label: 'Payouts' },
  { value: 'redemption', label: 'Redemption' },
  { value: 'login', label: 'Login' },
  { value: 'ui', label: 'UI' },
  { value: 'other', label: 'Other' },
  { value: 'unknown', label: 'Unknown' }
];

const PLATFORM_OPTIONS: Array<{ value: FeedbackPlatform | ''; label: string }> = [
  { value: '', label: 'All platforms' },
  { value: 'windows', label: 'Windows' },
  { value: 'mac', label: 'Mac' },
  { value: 'linux', label: 'Linux' }
];

// ─── Stats overview ───────────────────────────────────────────────────────

function StatsOverview() {
  const { data, isLoading } = useFeedbackStats();
  const stats = data?.byStatus || {};
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-5 lg:gap-4">
      <StatCard
        label="Open"
        value={stats.open || 0}
        color="text-amber-300"
        loading={isLoading}
      />
      <StatCard
        label="In progress"
        value={stats.in_progress || 0}
        color="text-sky-300"
        loading={isLoading}
      />
      <StatCard
        label="Auto-replied"
        value={stats.auto_replied || 0}
        color="text-emerald-300"
        loading={isLoading}
      />
      <StatCard
        label="Resolved"
        value={stats.resolved || 0}
        loading={isLoading}
      />
      <StatCard
        label="Last 24h"
        value={data?.last24h || 0}
        color="text-amber-200"
        loading={isLoading}
      />
    </div>
  );
}

// ─── Detail modal ─────────────────────────────────────────────────────────

interface DetailModalProps {
  reportId: string;
  onClose: () => void;
}

function DetailModal({ reportId, onClose }: DetailModalProps) {
  // Refetch the full report (with diagnostics + appUser populated)
  // — the list endpoint trims those fields to keep its payload small.
  const { data: report, isLoading: isFetching } = useFeedbackOne(reportId);
  const update = useUpdateFeedback();
  const [replyText, setReplyText] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [category, setCategory] = useState<FeedbackCategory>('unknown');
  const [error, setError] = useState<string | null>(null);

  // Seed editable fields when the detail data arrives.
  useEffect(() => {
    if (report) {
      setReplyText(report.replyText || '');
      setInternalNotes(report.internalNotes || '');
      setCategory(report.category);
    }
  }, [report]);

  // Esc closes
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  if (isFetching || !report) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        onClick={onClose}
      >
        <div className="w-full max-w-2xl rounded-2xl border border-nurev-border bg-nurev-surface-lift p-6">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="mt-3 h-24 w-full" />
        </div>
      </div>
    );
  }

  const publisher: PublisherRef | null =
    typeof report.publisherId === 'object' && report.publisherId !== null
      ? (report.publisherId as PublisherRef)
      : null;
  const app: AppRef | null =
    typeof report.appId === 'object' && report.appId !== null
      ? (report.appId as AppRef)
      : null;

  const diagnostics = report.diagnostics || {};
  const hasDiagnostics = Object.keys(diagnostics).length > 0;
  const ticketRef = String(report._id).slice(-8).toUpperCase();
  const isTerminal = report.status === 'resolved' || report.status === 'spam';

  const handleSaveReply = async () => {
    setError(null);
    try {
      await update.mutateAsync({
        id: report._id,
        replyText,
        internalNotes,
        category
      });
      // Stay open so admin can see the saved state; status will be
      // bumped to 'in_progress' server-side if it was 'open'.
    } catch (err) {
      setError(extractErr(err));
    }
  };

  const handleStatusAction = async (status: FeedbackStatus) => {
    setError(null);
    try {
      await update.mutateAsync({
        id: report._id,
        status,
        internalNotes,
        category
      });
      onClose();
    } catch (err) {
      setError(extractErr(err));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[88vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-nurev-border bg-nurev-surface-lift p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-nurev-text">
              Report #{ticketRef}
            </h3>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge value={STATUS_LABEL[report.status]} />
              <span className="rounded bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-300 ring-1 ring-amber-500/30">
                {CATEGORY_LABEL[report.category]}
              </span>
              {report.platform && (
                <span className="rounded bg-white/[0.06] px-2 py-0.5 text-[10px] font-medium text-nurev-text-soft">
                  {report.platform}
                </span>
              )}
              {report.replySource === 'ai' && (
                <span className="rounded bg-fuchsia-500/15 px-2 py-0.5 text-[10px] font-medium text-fuchsia-300 ring-1 ring-fuchsia-500/30">
                  AI auto-reply
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-nurev-muted hover:bg-white/5 hover:text-nurev-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Original user message */}
        <Card className="mb-4 bg-nurev-surface">
          <SectionLabel>User reported</SectionLabel>
          <p className="whitespace-pre-wrap text-sm text-nurev-text">
            {report.message}
          </p>
          <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-nurev-muted">
            {report.userCode && <span>user: {report.userCode}</span>}
            {report.replyEmail && <span>reply-to: {report.replyEmail}</span>}
            {report.appVersion && <span>app v{report.appVersion}</span>}
            <span>{fmtDateTime(report.createdAt)}</span>
          </div>
        </Card>

        {/* Publisher/app context */}
        {(publisher || app) && (
          <Card className="mb-4 bg-nurev-surface">
            <SectionLabel>Context</SectionLabel>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {publisher && (
                <KV label="Publisher">
                  {publisher.companyName || publisher.name || publisher.email || '—'}
                </KV>
              )}
              {app && <KV label="App">{app.name || app.appKey || '—'}</KV>}
              {app?.currencyName && (
                <KV label="Currency">{app.currencyName}</KV>
              )}
            </div>
          </Card>
        )}

        {/* Diagnostics from the desktop client */}
        {hasDiagnostics && (
          <Card className="mb-4 bg-nurev-surface">
            <SectionLabel>Diagnostics</SectionLabel>
            <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-nurev-text-soft">
              {JSON.stringify(diagnostics, null, 2)}
            </pre>
          </Card>
        )}

        {/* Reply editor — pre-filled with Claude auto-reply if present */}
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Category
            </label>
            <Select<FeedbackCategory>
              value={category}
              onChange={v => setCategory(v)}
              options={CATEGORY_OPTIONS.filter(
                o => o.value !== ''
              ).map(o => ({ value: o.value as FeedbackCategory, label: o.label }))}
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Reply to user
              {report.replySource === 'ai' && (
                <span className="ml-2 text-fuchsia-300">
                  (pre-filled by AI auto-triage — review before saving)
                </span>
              )}
              {!report.replyEmail && (
                <span className="ml-2 text-amber-300">
                  (no reply-to on file — saving sets in-app status only)
                </span>
              )}
            </label>
            <textarea
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              disabled={isTerminal}
              rows={8}
              className="w-full resize-none rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text placeholder:text-nurev-muted-dim focus:border-amber-500/50 focus:outline-none disabled:opacity-50"
              placeholder="Plain-text reply. White-label safe: don't mention NUREV — say 'the platform team'."
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Internal notes (never shown to user)
            </label>
            <textarea
              value={internalNotes}
              onChange={e => setInternalNotes(e.target.value)}
              disabled={isTerminal}
              rows={3}
              className="w-full resize-none rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text placeholder:text-nurev-muted-dim focus:border-amber-500/50 focus:outline-none disabled:opacity-50"
              placeholder="Notes for next admin (audit logged)…"
            />
          </div>

          {error && <p className="text-xs text-red-300">{error}</p>}

          {!isTerminal && (
            <div className="flex flex-wrap gap-2 pt-1">
              <ActionButton
                variant="info"
                size="sm"
                disabled={update.isPending}
                onClick={handleSaveReply}
              >
                {report.replyEmail ? 'Save & send reply' : 'Save (no email)'}
              </ActionButton>
              <ActionButton
                variant="positive"
                size="sm"
                disabled={update.isPending}
                onClick={() => handleStatusAction('resolved')}
              >
                Resolve
              </ActionButton>
              <ActionButton
                variant="neutral"
                size="sm"
                disabled={update.isPending}
                onClick={() => handleStatusAction('awaiting_user')}
              >
                Awaiting user
              </ActionButton>
              <ActionButton
                variant="destructive"
                size="sm"
                disabled={update.isPending}
                onClick={() => handleStatusAction('spam')}
              >
                Spam
              </ActionButton>
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between text-[10px] text-nurev-muted">
          <span>Created {fmtDateTime(report.createdAt)}</span>
          {report.repliedAt && (
            <span>Replied {fmtDateTime(report.repliedAt)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-nurev-muted">{label}:</span>{' '}
      <span className="text-nurev-text">{children}</span>
    </div>
  );
}

function extractErr(err: unknown): string {
  return (
    (err as { response?: { data?: { error?: string } } })?.response?.data
      ?.error || 'Update failed.'
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

export function FeedbackScreen() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | ''>('open');
  const [categoryFilter, setCategoryFilter] = useState<FeedbackCategory | ''>(
    ''
  );
  const [platformFilter, setPlatformFilter] = useState<FeedbackPlatform | ''>(
    ''
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const params = useMemo(
    () => ({
      page,
      limit: 20,
      sort: '-createdAt',
      status: statusFilter,
      category: categoryFilter,
      platform: platformFilter
    }),
    [page, statusFilter, categoryFilter, platformFilter]
  );
  const { data, isLoading } = useFeedback(params);
  const items = data?.docs || [];
  const totalPages = data?.totalPages || 1;

  const resetPage = () => setPage(1);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
            Feedback
          </h1>
          <p className="mt-1 text-sm text-nurev-muted">
            End-user reports from the desktop Rewards Hub. Open = needs a
            human. Auto-replied = Claude handled it; review if anything looks
            off.
          </p>
        </div>
      </header>

      <StatsOverview />

      <Card className="!p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[160px]">
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Status
            </label>
            <Select<FeedbackStatus | ''>
              value={statusFilter}
              onChange={v => {
                setStatusFilter(v);
                resetPage();
              }}
              options={STATUS_OPTIONS.map(o => ({
                value: o.value,
                label: o.label
              }))}
            />
          </div>
          <div className="min-w-[160px]">
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Category
            </label>
            <Select<FeedbackCategory | ''>
              value={categoryFilter}
              onChange={v => {
                setCategoryFilter(v);
                resetPage();
              }}
              options={CATEGORY_OPTIONS.map(o => ({
                value: o.value,
                label: o.label
              }))}
            />
          </div>
          <div className="min-w-[160px]">
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Platform
            </label>
            <Select<string>
              value={platformFilter || ''}
              onChange={v => {
                setPlatformFilter((v || '') as FeedbackPlatform | '');
                resetPage();
              }}
              options={PLATFORM_OPTIONS.map(o => ({
                value: (o.value || '') as string,
                label: o.label
              }))}
            />
          </div>
        </div>
      </Card>

      <Card className="!p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-nurev-border text-sm">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-wider text-nurev-muted">
              <tr>
                <th className="px-3 py-2 text-left">Ref</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-left">Message</th>
                <th className="px-3 py-2 text-left">Platform</th>
                <th className="px-3 py-2 text-left">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-nurev-border">
              {isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6">
                    <Skeleton className="h-5 w-full" />
                  </td>
                </tr>
              )}
              {!isLoading && items.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-8 text-center text-sm text-nurev-muted"
                  >
                    No reports match the current filters.
                  </td>
                </tr>
              )}
              {items.map(r => {
                const ref = String(r._id).slice(-8).toUpperCase();
                const preview =
                  r.message.length > 100
                    ? `${r.message.slice(0, 97)}…`
                    : r.message;
                return (
                  <tr
                    key={r._id}
                    onClick={() => setSelectedId(r._id)}
                    className="cursor-pointer hover:bg-white/[0.03]"
                  >
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-nurev-muted">
                      #{ref}
                    </td>
                    <td className="px-3 py-2">
                      <Badge value={STATUS_LABEL[r.status]} />
                    </td>
                    <td className="px-3 py-2 text-nurev-text-soft">
                      {CATEGORY_LABEL[r.category]}
                    </td>
                    <td className="px-3 py-2 text-nurev-text">{preview}</td>
                    <td className="px-3 py-2 text-nurev-muted">
                      {r.platform || '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-nurev-muted">
                      {fmtDate(r.createdAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      )}

      {selectedId && (
        <DetailModal
          reportId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
